import React, { useState, useEffect } from "react";
import { ArrowRight, ChevronDown, ChevronUp, Star, Phone } from "lucide-react";
import {
  ShieldCheck,
  Rocket,
  Lightning,
  Seal,
  CheckCircle,
  Timer,
  Headset,
  CurrencyDollar,
} from "@phosphor-icons/react";
import ContactForm from "../components/ContactForm";
import ContactModal from "../components/ContactModal";
import SEO from "../components/SEO";
import PricingTabsSection from "../components/PricingTabsSection";
import { useScrollReveal } from "../hooks/useScrollReveal";

const Reveal = ({ children, direction = "up", delay = 0, className = "" }) => {
  const [ref, visible] = useScrollReveal();
  const animClass =
    direction === "left"
      ? "anim-slide-left"
      : direction === "right"
        ? "anim-slide-right"
        : direction === "scale"
          ? "anim-scale-in"
          : "anim-fade-up";
  const delayClass = delay ? `anim-delay-${delay}` : "";
  return (
    <div
      ref={ref}
      className={`${visible ? `${animClass} ${delayClass}` : "anim-hidden"} max-w-full ${className}`}
    >
      {children}
    </div>
  );
};

const LandingPage = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const row1 = [
    {
      name: "Natalie Brooks",
      role: "Marketing Manager, Sterling Digital Group",
      image: "https://randomuser.me/api/portraits/women/65.jpg",
      text: "The Trademark Facility team made the entire registration process simple and stress-free. They kept me informed from start to finish.",
    },
    {
      name: "Brandon Lewis",
      role: "Founder, Elevate Apparel",
      image: "https://randomuser.me/api/portraits/men/45.jpg",
      text: "My Amazon Brand Registry was completed along with my trademark filing much faster than I expected. Excellent service.",
    },
    {
      name: "Jason Miller",
      role: "Product Director, Vertex Innovations",
      image: "https://randomuser.me/api/portraits/men/22.jpg",
      text: "Their patent specialists handled everything professionally. I appreciated the clear communication and attention to detail.",
    },
    {
      name: "Grace Mitchell",
      role: "Author & Publisher",
      image: "https://randomuser.me/api/portraits/women/32.jpg",
      text: "Copyright registration for my publications was completed smoothly. The team was knowledgeable and always available to help.",
    },
    {
      name: "Nathan Cooper",
      role: "Restaurant Owner",
      image: "https://randomuser.me/api/portraits/men/72.jpg",
      text: "A business started using a name similar to mine, but my registered trademark made resolving the issue straightforward.",
    },
  ];
  const row2 = [
    {
      name: "Victoria Hayes",
      role: "Founder, Luxe Cart",
      image: "https://randomuser.me/api/portraits/women/50.jpg",
      text: "My trademark application was submitted quickly, and I received updates throughout every stage of the process.",
    },
    {
      name: "Tyler Bennett",
      role: "Mobile App Developer",
      image: "https://randomuser.me/api/portraits/men/55.jpg",
      text: "I had no experience with trademarks, but they explained everything clearly and handled all the paperwork for me.",
    },
    {
      name: "Madison Reed",
      role: "Boutique Owner",
      image: "https://randomuser.me/api/portraits/women/79.jpg",
      text: "Protecting my boutique's name was incredibly easy with Trademark Facility. Their support exceeded my expectations.",
    },
    {
      name: "Christopher Allen",
      role: "CEO, Horizon Hospitality Group",
      image: "https://randomuser.me/api/portraits/men/36.jpg",
      text: "We filed several trademarks for our growing business, and every application was handled with professionalism and care.",
    },
    {
      name: "Samantha Foster",
      role: "Fashion Designer",
      image: "https://randomuser.me/api/portraits/women/41.jpg",
      text: "Affordable pricing, responsive support, and outstanding legal guidance. I highly recommend Trademark Facility.",
    },
  ];

  const faqs = [
    {
      question: "How quickly can my trademark application be filed?",
      answer:
        "After receiving your information, our legal team reviews your application and can prepare and submit it to the USPTO within one business day with our express filing service.",
    },
    {
      question: "What is included in the $45 Starter Plan?",
      answer:
        "The Starter Plan includes an initial case evaluation, a basic trademark search, attorney-guided filing assistance, and preparation of your trademark application. USPTO filing fees are charged separately.",
    },
    {
      question: "Do you guarantee trademark approval?",
      answer:
        "No company can legally guarantee USPTO approval. However, our experienced attorneys carefully review every application to reduce the risk of refusal and improve your chances of successful registration.",
    },
    {
      question: "Can I register both my business name and logo?",
      answer:
        "Yes. Your business name and logo can both be protected, but they are generally filed as separate trademark applications because each is treated as a distinct trademark.",
    },
    {
      question: "When will I receive my trademark registration certificate?",
      answer:
        "The USPTO review process typically takes several months. Once your application is approved and successfully registered, you'll receive your official federal trademark registration certificate.",
    },
    {
      question: "What happens if I receive an Office Action from the USPTO?",
      answer:
        "If the USPTO issues an Office Action requesting additional information or raising concerns, our legal team will review the notice and help prepare an appropriate response based on the coverage included in your selected service package.",
    },
  ];

  return (
    <div className="min-h-screen font-body overflow-x-hidden">
      <SEO
        title="Register Your Trademark Today"
        description="Start your trademark registration for just $45. Fast, affordable, attorney-backed trademark filing across all 50 states."
        path="/lp"
        keywords="trademark registration, file trademark, USPTO, brand protection"
      />

      {/* ═══════ HERO — gradient bg with angled form ═══════ */}
      <section
        className="relative bg-gradient-to-br from-primary via-primary-light to-primary overflow-hidden"
        data-testid="hero-section"
      >
        <img
          src="/assets/img/mix/main-banner-lp.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/60 to-primary/70" />
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-lime/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-lime/5 rounded-full translate-y-1/2 -translate-x-1/3" />
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20 lg:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-16 items-center overflow-hidden">
            <div className="lg:hidden w-full max-w-full overflow-hidden">
              <ContactForm
                title={
                  <>
                    TRADEMARK REGISTRATION <br />
                    ONLY <span className="blink-price text-lime">$45</span>
                  </>
                }
                subtitle="Protect your brand with trusted expertise"
                buttonText="Submit"
              />
            </div>
            <Reveal direction="left" className="min-w-0">
              <span className="inline-block bg-lime/10 border border-lime/30 text-lime text-xs font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-6">
                Limited-Time Offer
              </span>
              <h1 className="font-heading text-3xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-white tracking-tighter leading-[1.1] mb-5">
                Protect Your Brand
                <span className="text-lime"> Before Someone</span> Else Does.
              </h1>
              <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-8 max-w-lg">
                Every year, hundreds of thousands of trademark applications are
                submitted to the USPTO. Delaying your registration could put
                your business name, logo, or slogan at risk. Secure your
                intellectual property today with professional legal
                support—starting from just $45.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-3 mb-8">
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <CheckCircle
                    size={18}
                    weight="fill"
                    className="text-lime shrink-0"
                  />{" "}
                  Attorney-Guided Filing
                </div>
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <CheckCircle
                    size={18}
                    weight="fill"
                    className="text-lime shrink-0"
                  />{" "}
                  Fast 24-Hour Processing
                </div>
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <CheckCircle
                    size={18}
                    weight="fill"
                    className="text-lime shrink-0"
                  />{" "}
                  100% Money-Back Guarantee
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                data-testid="hero-cta-btn"
                className="group inline-flex items-center bg-lime hover:bg-lime-light text-white font-bold py-2.5 px-6 rounded-xl transition-all duration-300 active:scale-95 shadow-[0_0_30px_rgba(241,80,37,0.3)]"
              >
                Claim My Trademark Now
                <ArrowRight className="ml-2 h-5 w-5 text-white group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </Reveal>
            <Reveal direction="right" className="hidden lg:block wd-70">
              <ContactForm
                title={<>Register My Trademark Today</>}
                subtitle="Protect your brand with trusted expertise"
                buttonText="Submit"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ TRUST STRIP ═══════ */}
      <section
        className="bg-white border-b border-gray-100 py-6"
        data-testid="trust-strip"
      >
        <div className="max-w-5xl mx-auto px-4 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs sm:text-sm text-gray-500 font-medium">
          <span className="flex items-center gap-2">
            <Seal size={18} weight="duotone" className="text-lime" /> USPTO
            Authorized
          </span>
          <span className="flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" className="text-lime" />{" "}
            100% Secure & Confidential
          </span>
          <span className="flex items-center gap-2">
            <Timer size={18} weight="duotone" className="text-lime" /> 5-Minute
            Application
          </span>
          <span className="flex items-center gap-2">
            <Headset size={18} weight="duotone" className="text-lime" />{" "}
            Dedicated Attorney
          </span>
        </div>
      </section>

      {/* ═══════ SPLIT: RISK — image first on mobile ═══════ */}
      <section className="bg-gray-50 py-14 md:py-28" data-testid="risk-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-stretch overflow-hidden">
            <div className="order-2 md:order-1 flex flex-col justify-center">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Don't leave your brand unprotected.
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mt-2 mb-4">
                Why Trademark Registration Matters
              </h2>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-6">
                Without a federally registered trademark, your business name,
                logo, or slogan may be vulnerable to unauthorized use.
                Competitors could create confusion in the marketplace and weaken
                the reputation you've worked hard to build. If another party
                secures registration before you, you may be forced to change
                your branding and incur significant costs. Registering your
                trademark gives you exclusive nationwide rights and the legal
                authority to protect your brand against infringement.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="group inline-flex items-center bg-lime hover:bg-lime-light text-white font-bold py-2.5 px-5 rounded-xl transition-all duration-300 active:scale-95 text-sm self-start"
              >
                Secure My Brand{" "}
                <ArrowRight className="ml-2 h-4 w-4 text-white group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
            <Reveal direction="right" className="order-1 md:order-2 flex">
              <img
                src="/assets/img/mix/lp-sec-01.webp"
                alt="Business owner reviewing documents"
                className="w-full h-[250px] md:h-auto object-cover rounded-2xl"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ HOW IT WORKS — numbered cards with icon bg ═══════ */}
      <section
        className="bg-white py-14 md:py-28"
        data-testid="process-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Easy Process
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
                Register your trademark in just 3 simple steps.
              </h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-0">
            {[
              {
                num: "1",
                icon: Rocket,
                title: "Complete Your Application",
                desc: "Provide your business name, logo, slogan, or other trademark details along with your contact information. The form takes only a few minutes to complete.",
              },
              {
                num: "2",
                icon: ShieldCheck,
                title: "Attorney Review & Filing",
                desc: "Our experienced attorneys carefully review your application, perform a comprehensive trademark search, and submit your filing to the USPTO.",
              },
              {
                num: "3",
                icon: Seal,
                title: "Secure Your Brand",
                desc: "Once your trademark is registered, you'll receive your official registration certificate and enjoy nationwide legal protection for your brand.",
              },
            ].map((step, i) => (
              <Reveal key={i} delay={i + 1}>
                <div
                  className={`relative p-10 h-full text-center transition-all duration-500 hover:bg-primary hover:text-white group ${i < 2 ? "md:border-r border-b md:border-b-0 border-gray-200" : ""}`}
                  data-testid={`lp-step-${i}`}
                >
                  <div className="w-14 h-14 rounded-full bg-primary text-lime font-heading text-2xl font-extrabold flex items-center justify-center mx-auto mb-5 group-hover:bg-lime group-hover:text-primary transition-colors duration-300">
                    {step.num}
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-3 group-hover:text-white transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm group-hover:text-gray-300 transition-colors duration-300">
                    {step.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ SPLIT: EXPERTISE (image left | text right) ═══════ */}
      <section
        className="bg-primary py-14 md:py-28"
        data-testid="expertise-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-stretch overflow-hidden">
            <Reveal direction="left" className="flex">
              <img
                src="/assets/img/mix/lp-sec-02.webp"
                alt="Attorneys discussing documents"
                className="w-full h-[250px] md:h-auto object-cover rounded-2xl"
              />
            </Reveal>
            <Reveal direction="right" className="flex flex-col justify-center">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Why Choose Trademark Facility
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight mt-2 mb-4">
                Dedicated legal professionals committed to protecting your
                brand.
              </h2>
              <p className="text-gray-400 leading-relaxed text-sm sm:text-base mb-6">
                Trademark Facility is more than a filing service. Every
                application is carefully reviewed by an experienced intellectual
                property attorney with extensive trademark knowledge. We perform
                detailed trademark searches, provide guidance throughout the
                registration process, assist with office actions based on your
                selected package, and help safeguard your trademark after
                registration. Your brand deserves trusted legal support every
                step of the way.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="group inline-flex items-center bg-lime hover:bg-lime-light text-white font-bold py-2.5 px-5 rounded-xl transition-all duration-300 active:scale-95 text-sm self-start"
              >
                Talk to an Attorney{" "}
                <ArrowRight className="ml-2 h-4 w-4 text-white group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ PRICING — tabbed self-filing / attorney-led ═══════ */}
      <PricingTabsSection
        sectionBg="bg-gray-50"
        headerEyebrow="Choose Your Plan"
        onCtaClick={() => setIsModalOpen(true)}
      />

      {/* ═══════ TESTIMONIALS — dual marquee rows ═══════ */}
      <section
        className="bg-primary py-14 md:py-28 overflow-hidden"
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mb-14">
          <Reveal>
            <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
              Testimonials
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight mt-3">
              What Our Clients Say
            </h2>
          </Reveal>
        </div>

        {/* Row 1 → scrolls RIGHT */}
        <div className="relative mb-6">
          <div className="flex marquee-right w-max gap-6">
            {[...row1, ...row1].map((t, i) => (
              <div
                key={i}
                className="relative bg-white/5 border border-white/10 rounded-2xl p-6 w-[380px] shrink-0 transition-all duration-300 hover:bg-white/10 hover:border-lime/30 group"
                data-testid={
                  i < row1.length ? `lp-testimonial-r1-${i}` : undefined
                }
              >
                <div className="absolute top-4 right-6 font-heading text-5xl font-extrabold text-lime/10 leading-none select-none group-hover:text-lime/20 transition-colors duration-300">
                  "
                </div>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-3.5 w-3.5 text-lime fill-lime" />
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed text-sm mb-5 relative z-10 line-clamp-3">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-lime/40"
                  />
                  <div>
                    <div className="font-bold text-white text-sm">{t.name}</div>
                    <div className="text-lime text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 2 → scrolls LEFT */}
        <div className="relative">
          <div className="flex marquee-left w-max gap-6">
            {[...row2, ...row2].map((t, i) => (
              <div
                key={i}
                className="relative bg-white/5 border border-white/10 rounded-2xl p-6 w-[380px] shrink-0 transition-all duration-300 hover:bg-white/10 hover:border-lime/30 group"
                data-testid={
                  i < row2.length ? `lp-testimonial-r2-${i}` : undefined
                }
              >
                <div className="absolute top-4 right-6 font-heading text-5xl font-extrabold text-lime/10 leading-none select-none group-hover:text-lime/20 transition-colors duration-300">
                  "
                </div>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-3.5 w-3.5 text-lime fill-lime" />
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed text-sm mb-5 relative z-10 line-clamp-3">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-lime/40"
                  />
                  <div>
                    <div className="font-bold text-white text-sm">{t.name}</div>
                    <div className="text-lime text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section className="bg-white py-14 md:py-28" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12">
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Have Questions?
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
                Find clear answers to our most frequently asked questions.
              </h2>
            </div>
          </Reveal>
          <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-200 overflow-hidden">
            {faqs.map((faq, i) => (
              <div key={i} data-testid={`lp-faq-${i}`}>
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex justify-between items-center py-5 px-6 text-left group transition-all duration-200 hover:bg-lime/5 hover:pl-8"
                >
                  <span className="font-heading font-bold pr-4 group-hover:text-lime transition-colors duration-200">
                    {faq.question}
                  </span>
                  {activeFaq === i ? (
                    <ChevronUp className="h-5 w-5 text-lime shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                  )}
                </button>
                {activeFaq === i && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed text-sm">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FINAL CTA — bold with urgency ═══════ */}
      <section
        className="relative bg-[#07382f] py-14 md:py-28 overflow-hidden"
        data-testid="cta-section"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-lime/5 rounded-full" />
        </div>
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 text-center relative z-10">
          <Reveal>
            <span className="inline-block bg-lime/10 border border-lime/30 text-lime text-xs font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-6">
              Take Action
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-5">
              Protect your brand before it's too late.
            </h2>
            <p className="text-sm sm:text-lg md:text-xl text-lime-100 max-w-3xl mx-auto">
              Begin your trademark registration in just a few minutes for only
              $45. Secure your business name, logo, or slogan today and take the
              first step toward protecting what you've built with Trademark
              Facility.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
              <button
                onClick={() => setIsModalOpen(true)}
                data-testid="final-cta-btn"
                className="group inline-flex items-center justify-center bg-lime text-white hover:bg-lime-light font-bold py-2.5 px-6 rounded-xl transition-all duration-300 hover:shadow-[0_0_40px_rgba(241,80,37,0.4)] active:scale-95"
              >
                File My Trademark — $45
                <ArrowRight className="ml-2 h-5 w-5 text-white group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <a
                href="tel:+18776340302"
                className="inline-flex items-center justify-center border-2 border-white/20 hover:border-lime/50 text-white hover:text-lime font-bold py-2.5 px-6 rounded-xl transition-all duration-300"
              >
                <Phone className="mr-2 h-5 w-5" /> Call Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Start Your Trademark Registration"
      />
    </div>
  );
};

export default LandingPage;
